import type { EventFieldView } from "common";
import EventRecordPicker from "./event-record-picker";
import { type FieldValue, refCollectionName } from "./event-form-state";

// Copied from form-input-component.tsx so event fields match other forms.
const INPUT_CLASS =
  "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6";

interface Props {
  canvasId: string;
  field: EventFieldView;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  disabled?: boolean;
  invalid?: boolean;
  /** Shown in an empty reference field that the server will link on save. */
  linkHint?: string | null;
  inputId: string;
}

const choicesOf = (field: EventFieldView): string[] | null =>
  field.enum ?? (field.ontology ? field.ontologyOptions : null);

const EventFieldInput = ({
  canvasId,
  field,
  value,
  onChange,
  disabled,
  invalid,
  linkHint,
  inputId,
}: Props) => {
  const className = `${INPUT_CLASS} ${invalid ? "ring-red-500" : "ring-gray-300"}`;
  const common = {
    id: inputId,
    disabled,
    "aria-invalid": invalid ? true : undefined,
  } as const;
  const text = typeof value === "string" ? value : "";
  const choices = choicesOf(field);
  const refCollection = refCollectionName(canvasId, field.ref);

  let input: JSX.Element;
  if (field.type === "Boolean") {
    input = (
      <input
        {...common}
        type="checkbox"
        className="h-4 w-4 rounded border-gray-300 text-indigo-600"
        checked={value === true}
        onChange={e => onChange(e.target.checked)}
      />
    );
  } else if (refCollection) {
    input = (
      <EventRecordPicker
        canvasId={canvasId}
        collectionName={refCollection}
        value={text || null}
        inputId={inputId}
        disabled={disabled}
        placeholder={linkHint ?? "Choose a record"}
        onChange={recordId => onChange(recordId ?? "")}
      />
    );
  } else if (choices) {
    input = (
      <select
        {...common}
        className={className}
        value={text}
        onChange={e => onChange(e.target.value)}>
        <option value="" />
        {choices.map(c => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    );
  } else if (field.type === "Mixed") {
    input = (
      <textarea
        {...common}
        className={`${className} font-mono`}
        rows={4}
        value={text}
        onChange={e => onChange(e.target.value)}
      />
    );
  } else {
    input = (
      <input
        {...common}
        className={className}
        type={
          field.type === "Number"
            ? "number"
            : field.type === "Date"
              ? "date"
              : "text"
        }
        value={text}
        onChange={e => onChange(e.target.value)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
        {field.label}
        {field.required && <span className="ml-0.5 text-red-600">*</span>}
      </label>
      {input}
    </div>
  );
};

export default EventFieldInput;
